import { type MutableCollabFilter } from '@databases/model/views/view-filter';
import { type DatabaseFilter } from '@databases/types/database/view';

import { isUsageFilter, type UsageFilter } from './isUsageFilter';
import { getParentByChildId } from './search';

/**
  Retrieves a direct parent filter based on the provided child filter. The filter is
  retrieved from the list of filters stored in the view of a database. Two appraoches
  are used to retrieve the filter:

    1. The index the child filter is within the parent filter is provided when a filter
    attribute is altered or a filter is deleted. The index is provided when the child
    filter is retrieved, using the index to make a quick look up for the child filter if
    it is a boolean usage filter.

    2. Linear search is performed on the provided filter group, determining if a parent
    filter with the provided child filter exists.
  
  @param topLevelFilter - the boolean usage filter that encompasses all filters for a
  database view
  @param childFilter - the child filter whose parent filter should be retrieved
  @param indexInParent - the expected index for the child filter within the parent index
  used as a baseline to find the parent filter in the filters array

  @returns {UsageFilter} - the boolean usage filter representing a filter group who has
  a child filter matching the provided child filter
  @returns {undefined} - when the provided child filter no longer exists in the list of
  filters stored in the view of a database 
*/
export function getDirectParentFilter(
  topLevelFilter: UsageFilter,
  childFilter: MutableCollabFilter | DatabaseFilter,
  indexInParent: number
): UsageFilter | undefined {
  const filters = topLevelFilter.value;

  // Filter groups are restricted to a depth of one, meaning that if the child filter
  // is a boolean usage filter and therefore a filter group, it will be within the filter
  // that encompasses all filters for a database view
  if (isUsageFilter(childFilter)) {
    if (filters[indexInParent].id === childFilter.id) {
      return topLevelFilter;
    }
  }

  // Use linear search to find the parent filter with the given child id if one exists
  // and the child filter was not a filter group
  return getParentByChildId(topLevelFilter, childFilter.id);
}
